import { useState } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  CardFooter,
  Button,
  Badge,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useReplyToTicket, type TicketComment, type TicketStatus } from '@/api/support';
import { TERMINAL_STATUSES, formatDateTime } from '../shared';
import { TicketMarkdown } from './TicketMarkdown';

interface TicketThreadProps {
  ticketId: number;
  status: TicketStatus;
  comments: TicketComment[];
  /** Optional opening message (the original request) rendered before replies. */
  request?: { body: string; date: string };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ author, name }: { author: 'customer' | 'support'; name?: string }) {
  const isCustomer = author === 'customer';
  const text = isCustomer ? 'You' : name ? initials(name) : 'VN';
  return (
    <span
      aria-hidden
      style={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        fontSize: 11,
        fontWeight: 700,
        color: isCustomer ? 'var(--vn-accent-fg)' : 'var(--vn-fg-default)',
        background: isCustomer ? 'var(--vn-accent-solid, var(--vn-fg-default))' : 'var(--vn-bg-muted, var(--vn-bg-subtle))',
        border: isCustomer ? 'none' : '1px solid var(--vn-border-default)',
      }}
    >
      {text}
    </span>
  );
}

function Message({
  author,
  name,
  body,
  date,
  eyebrow,
}: {
  author: 'customer' | 'support';
  name?: string;
  body: string;
  date: string;
  eyebrow?: string;
}) {
  const displayName = author === 'customer' ? 'You' : name || 'Support';
  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'flex-start' }}>
      <Avatar author={author} name={name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-1)' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{displayName}</span>
          {eyebrow ? (
            <Badge tone="neutral" appearance="subtle" size="sm">
              {eyebrow}
            </Badge>
          ) : null}
          <span style={{ fontSize: 12, color: 'var(--vn-fg-muted)' }}>{formatDateTime(date)}</span>
        </div>
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 'var(--vn-radius-md)',
            background: 'var(--vn-bg-subtle)',
            border: '1px solid var(--vn-border-default)',
          }}
        >
          <TicketMarkdown source={body} />
        </div>
      </div>
    </div>
  );
}

export function TicketThread({ ticketId, status, comments, request }: TicketThreadProps) {
  const { show } = useToast();
  const [body, setBody] = useState('');
  const closed = TERMINAL_STATUSES.includes(status);

  const reply = useReplyToTicket(ticketId, {
    onSuccess: () => {
      setBody('');
      show({ tone: 'success', title: 'Reply sent' });
    },
    onError: () => show({ tone: 'danger', title: 'Could not send reply' }),
  });

  function handleSubmit() {
    const trimmed = body.trim();
    if (!trimmed) return;
    reply.mutate(trimmed);
  }

  return (
    <Card>
      <CardHeader
        actions={
          <Badge tone="neutral" appearance="subtle" size="sm" startIcon={<MessageSquare size={13} />}>
            {comments.length + (request ? 1 : 0)}
          </Badge>
        }
      >
        <CardTitle>Conversation</CardTitle>
      </CardHeader>

      <CardBody>
        {request || comments.length > 0 ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
            {request ? (
              <Message
                author="customer"
                body={request.body || '(no description)'}
                date={request.date}
                eyebrow="Original request"
              />
            ) : null}
            {comments.map((c) => (
              <Message
                key={c.ticket_comment_id}
                author={c.author}
                name={c.author_name}
                body={c.body}
                date={c.created_at}
              />
            ))}
          </div>
        ) : (
          <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 14 }}>
            No replies yet — use the box below to start the conversation.
          </p>
        )}
      </CardBody>

      <CardFooter>
        {closed ? (
          <Alert tone="neutral" style={{ width: '100%' }}>
            This ticket is {status}. Open a new ticket to continue the conversation.
          </Alert>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-2)', width: '100%' }}
          >
            <Textarea
              rows={3}
              placeholder="Write a reply…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                startIcon={<Send size={15} />}
                disabled={!body.trim() || reply.isPending}
              >
                {reply.isPending ? 'Sending…' : 'Send reply'}
              </Button>
            </div>
          </form>
        )}
      </CardFooter>
    </Card>
  );
}
